import './Home.css'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import HeroSlider from '../components/HeroSlider'
import CategoryTiles from '../components/CategoryTiles'
import Carousel from '../components/Carousel'
import ProductCard from '../components/ProductCard'
import Repair from '../components/Repair'
import { useCategories, useProducts, useReviews } from '../hooks/useSiteData'

// Replace with your WhatsApp number: country code + number, digits only (e.g. 919876543210)
const WHATSAPP_NUMBER = '91XXXXXXXXXX'
const WHATSAPP_MSG = 'Hi iPOINT, I would like to know more.'

const TRUST = [
  ['Repair for Apple & Android', 'Screens, batteries, charging and more'],
  ['Doorstep pickup & delivery', 'We collect and return your phone'],
  ['Secure online payment', 'Pay with UPI, cards and netbanking via Razorpay'],
  
]

export default function Home() {
  const [email, setEmail] = useState('')
  const [done, setDone] = useState(false)
  const products = useProducts()
  const cats = useCategories()
  const reviews = useReviews()
  const newIphone = products.filter(p => p.models?.some(m => m.startsWith('iPhone 17') || m === 'iPhone Air'))
  const best = products.filter(p => p.best)
  const hasCharms = cats.some(c => c.name === 'Charms')

  // scroll to a section like /#repair when arriving from another page or clicking the nav link
  const { hash, key } = useLocation()
  useEffect(() => {
    if (!hash) return
    const el = document.getElementById(hash.slice(1))
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash, key])

  return (
    <div className="home">
      <HeroSlider />

      <section className="tagline" aria-label="Our promise">
        <p className="tagline__text">Where connectivity meets <span>luxury</span></p>
      </section>

      {cats.length > 0 && (
        <section className="section">
          <div className="sec-head"><h2 className="section__title">Shop by category</h2><Link className="link" to="/shop">View all products</Link></div>
          <CategoryTiles />
        </section>
      )}

      {newIphone.length > 0 && (
        <section className="section">
          <div className="sec-head">
            <div><h2 className="section__title">For the iPhone 17 series</h2><p>New arrivals</p></div>
            <Link className="link" to="/shop?brand=Apple">View all</Link>
          </div>
          <Carousel label="iPhone products">{newIphone.map(p => <ProductCard key={p.id} p={p} />)}</Carousel>
        </section>
      )}

      {hasCharms && (
        <section className="feature">
          <div>
            <h2 className="section__title">Finish the look, not just the phone.</h2>
            <p>Charms, straps and metal stickers turn a plain case into something that is yours.</p>
            <Link className="btn btn--cobalt" to="/shop?category=Charms">Shop charms</Link>
          </div>
          <div className="feature__art" aria-hidden="true"><i /><i /><i /></div>
        </section>
      )}

      {best.length > 0 && (
        <section className="section">
          <div className="sec-head"><h2 className="section__title">Best sellers</h2><Link className="link" to="/shop">View all</Link></div>
          <Carousel label="best sellers">{best.map(p => <ProductCard key={p.id} p={p} />)}</Carousel>
        </section>
      )}

      <section className="trust" aria-label="Why shop with iPOINT">
        <div className="trust__track">
          {[0, 1].map(copy => (
            <div className="trust__set" key={copy} aria-hidden={copy === 1}>
              {[...TRUST, ...TRUST].map(([t, s], i) => (
                <div className="trust__item" key={i}>
                  <strong style={{ animationDelay: `${-(i % TRUST.length) * 2}s` }}>{t}</strong>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <Repair />

      {reviews.length > 0 && (
        <section className="section">
          <h2 className="section__title">What customers say</h2>
          <div className="reviews">
            {reviews.map((r, i) => {
              const stars = Math.min(5, Math.max(0, Number(r.stars) || 0))
              return (
                <blockquote key={r.id ?? i}>
                  <p className="stars" aria-label={`${stars} out of 5`}>{'★'.repeat(stars)}</p>
                  <p>{r.text}</p>
                  <footer>{r.name}</footer>
                </blockquote>
              )
            })}
          </div>
        </section>
      )}

      <section className="news">
        <div><h2 className="section__title">Join our list</h2><p>New arrivals and offers, straight to your inbox.</p></div>
        {done ? <p className="news__ok" role="status">Thanks, you're on the list.</p> : (
          <form onSubmit={e => { e.preventDefault(); setDone(true) /* connect to your email service */ }}>
            <input type="email" required placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} aria-label="Email address" />
            <button className="btn btn--cobalt">Subscribe</button>
          </form>
        )}
      </section>

      <a
        className="wa"
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
      >
        <svg viewBox="0 0 32 32" width="30" height="30" fill="currentColor" aria-hidden="true">
          <path d="M16.04 3C9.4 3 4 8.4 4 15.03c0 2.12.56 4.19 1.61 6.01L4 29l8.14-1.57a12 12 0 0 0 3.9.65C22.68 28.08 28 22.68 28 16.05 28 9.4 22.68 3 16.04 3Zm0 22.06c-1.2 0-2.38-.24-3.48-.7l-.5-.2-4.83.93.95-4.7-.22-.5a9.9 9.9 0 0 1-1.6-5.36c0-5.5 4.5-9.97 10.05-9.97 5.5 0 9.97 4.47 9.97 9.97 0 5.5-4.47 10.03-10.34 10.03Zm5.5-7.47c-.3-.15-1.78-.88-2.05-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.95 1.18-.18.2-.35.22-.65.08-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.78-1.67-2.08-.18-.3-.02-.46.13-.6.13-.14.3-.35.45-.53.15-.17.2-.3.3-.5.1-.2.05-.38-.03-.53-.07-.15-.68-1.63-.93-2.24-.25-.58-.5-.5-.68-.5h-.58c-.2 0-.53.08-.8.38-.28.3-1.05 1.03-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.1 3.2 5.08 4.5.7.3 1.26.48 1.7.62.72.23 1.37.2 1.88.12.57-.08 1.78-.73 2.03-1.43.25-.7.25-1.3.17-1.43-.07-.12-.27-.2-.57-.35Z" />
        </svg>
      </a>
    </div>
  )
}